// ── Reports tab — report STATUS only, REPORT-EXCLUDED (Wave 3, REAL read) ────
// CRITICAL (blueprint §6 release gate #2 + this wave's gate): this tab is
// REPORT-EXCLUDED. It reads cohorts.reportReadiness (GET /cohorts/:id/report)
// which returns report generation STATUS only — NOT report bodies. NO report
// HTML / summary / highlights / action items are ever rendered here, for ANY
// role. The report download / generation lanes are operator-gated; a closed gate
// renders as `unavailable` / `gate-closed` (NOT empty, NOT an error) — a closed
// gate NEVER implies a generated report exists (no-fabrication).
//
// The deep WRITE flow (generate / regenerate a report) is OWNED BY WAVE 7
// (Reports) — NOT built here; no generate button is fabricated. Reading the
// actual report content (sanitized HTML, the 4-type taxonomy, SA-only
// regeneration) is the Reports module's job, behind its own visibility scoping.

import { Alert, Badge } from "@vjal/ui";

import { useHonestQuery } from "../../../components/honest-state";
import { getCoachingServices } from "../../../services/coaching-service-provider";
import { coachingQueryKeys } from "../../../hooks/coaching-query-keys";
import type { CoachingOperationResult } from "../../../services/coaching-dtos";
import { CohortTabShell } from "./cohort-tab-shell";

const services = getCoachingServices();

/** The readiness read returns an operation-shaped status envelope (no bodies). */
type ReportReadiness = CoachingOperationResult & {
  generationGate?: string | null;
  report_status?: string;
};

export function CohortReportsTab({ cohortId }: { cohortId: string }) {
  // errorPath disambiguates the by-design report 503 (gate-closed) from a real
  // domain outage (unavailable) in the CO_* posture map.
  const query = useHonestQuery<ReportReadiness>(
    [...coachingQueryKeys.cohortDetail(cohortId), "report-readiness"],
    () => services.cohorts.reportReadiness(cohortId) as Promise<ReportReadiness>,
    { errorPath: `/cohorts/${cohortId}/report` }
  );

  const readiness = query.data;
  const status = readiness?.report_status ?? readiness?.status ?? "unknown";
  const gateClosed = readiness?.generationGate === "closed";

  return (
    <CohortTabShell
      title="Reports"
      subtitle="Report generation status only. Report content is managed in the Reports area with its own visibility scoping."
      posture={query.posture}
      gate={query.gate}
      reference={query.reference}
      onRetry={query.retry}
    >
      <div className="space-y-4" data-testid="cohort-reports-tab">
        {/* REPORT-EXCLUDED: status + gate posture only — never report content. */}
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-strong">Generation status</span>
          <Badge variant={status === "generated" || status === "ready" ? "success" : "warning"}>{status}</Badge>
        </div>
        {gateClosed ? (
          <Alert variant="warning">
            Report generation is closed for this cohort right now. No reports are generated until the lane opens. No
            report content is shown here.
          </Alert>
        ) : (
          <Alert variant="info">
            This view shows report generation status only. Open the Reports area to read reports you're authorized to
            see; report content is never displayed on the cohort workspace.
          </Alert>
        )}
      </div>
    </CohortTabShell>
  );
}
